import {
    ActionButtonFixedColor,
    ActionButtonThemeableColor,
    ButtonFixedColor,
    ButtonThemeableColor,
    CarouselFixedColor,
    CarouselThemeableColor,
    ChipFixedColor,
    ChipThemeableColor,
    FlagFixedColor,
    FlagThemeableColor,
    HoneycombRadius,
    HoneycombShadowSize,
    IconButtonFixedColor,
    IconButtonThemeableColor,
    ProgressBarSize,
    ScoreFixedColor,
    ScoreThemeableColor,
    SeatIconThemeableColor,
    StampFixedColor,
    StampThemeableColor,
} from '../theming';
import {
    BrandColor,
    BackgroundColor,
    CommonColor,
    TextColor,
    RootColor,
    ColorOption,
    ColorTint,
    SystemColor,
    ColorShade,
} from '../theming/types/HoneycombPalette';
import { TypographyBodySize, TypographyHeadingSize, TypographyVariant } from '../theming/types/HoneycombTypography';

export function isThemeColor(value: string): value is ColorOption {
    const keys: string[] = [
        'primary',
        'secondary',
        'tertiary',
        'accent',
        'accentPrimary',
        'accentSecondary',
        'accentTertiary',
        'primary.base',
        'primary.light',
        'primary.lighter',
        'primary.lightest',
        'primary.dark',
        'primary.tint.100',
        'primary.tint.200',
        'primary.tint.300',
        'primary.tint.400',
        'primary.tint.500',
        'primary.tint.600',
        'primary.tint.700',
        'primary.contrastText',
        'secondary.base',
        'secondary.light',
        'secondary.lighter',
        'secondary.lightest',
        'secondary.dark',
        'secondary.tint.100',
        'secondary.tint.200',
        'secondary.tint.300',
        'secondary.tint.400',
        'secondary.tint.500',
        'secondary.tint.600',
        'secondary.tint.700',
        'secondary.contrastText',
        'tertiary.base',
        'tertiary.light',
        'tertiary.lighter',
        'tertiary.lightest',
        'tertiary.dark',
        'tertiary.tint.100',
        'tertiary.tint.200',
        'tertiary.tint.300',
        'tertiary.tint.400',
        'tertiary.tint.500',
        'tertiary.tint.600',
        'tertiary.tint.700',
        'tertiary.contrastText',
        'accent.base',
        'accent.light',
        'accent.lighter',
        'accent.lightest',
        'accent.dark',
        'accent.tint.100',
        'accent.tint.200',
        'accent.tint.300',
        'accent.tint.400',
        'accent.tint.500',
        'accent.tint.600',
        'accent.tint.700',
        'accentPrimary.base',
        'accentPrimary.light',
        'accentPrimary.lighter',
        'accentPrimary.lightest',
        'accentPrimary.dark',
        'accentPrimary.tint.100',
        'accentPrimary.tint.200',
        'accentPrimary.tint.300',
        'accentPrimary.tint.400',
        'accentPrimary.tint.500',
        'accentPrimary.tint.600',
        'accentPrimary.tint.700',
        'accentPrimary.contrastText',
        'accentSecondary.base',
        'accentSecondary.light',
        'accentSecondary.lighter',
        'accentSecondary.lightest',
        'accentSecondary.dark',
        'accentSecondary.tint.100',
        'accentSecondary.tint.200',
        'accentSecondary.tint.300',
        'accentSecondary.tint.400',
        'accentSecondary.tint.500',
        'accentSecondary.tint.600',
        'accentSecondary.tint.700',
        'accentSecondary.contrastText',
        'accentTertiary.base',
        'accentTertiary.light',
        'accentTertiary.lighter',
        'accentTertiary.lightest',
        'accentTertiary.dark',
        'accentTertiary.tint.100',
        'accentTertiary.tint.200',
        'accentTertiary.tint.300',
        'accentTertiary.tint.400',
        'accentTertiary.tint.500',
        'accentTertiary.tint.600',
        'accentTertiary.tint.700',
        'accentTertiary.contrastText',
        'grey.base',
        'grey.light',
        'grey.lighter',
        'grey.lightest',
        'grey.dark',
        'grey.tint.100',
        'grey.tint.200',
        'grey.tint.300',
        'grey.tint.400',
        'grey.tint.500',
        'grey.tint.600',
        'grey.tint.700',
        'common.white',
        'common.black',
        'system.success.base',
        'system.success.light',
        'system.success.lightest',
        'system.error.base',
        'system.error.light',
        'system.error.lightest',
        'system.warning.base',
        'system.warning.light',
        'system.warning.lightest',
        'system.info.base',
        'system.info.light',
        'system.info.lightest',
        'system.highlight.base',
        'system.highlight.light',
        'system.highlight.lightest',
        'text.body',
        'text.light',
        'text.inverse',
        'text.heading',
        'text.price',
        'text.link',
        'background.body',
        'background.content',
        'background.offset',
        'neutral.base',
        'neutral.light',
        'neutral.lighter',
        'neutral.lightest',
        'neutral.dark',
        'neutral.tint.100',
        'neutral.tint.200',
        'neutral.tint.300',
        'neutral.tint.400',
        'neutral.tint.500',
        'neutral.tint.600',
        'neutral.tint.700',
        'divider',
        'border',
        'starRating',
    ];
    return keys.indexOf(value) !== -1;
}

export function isExtendedColor(value: string): value is BrandColor | 'grey' | 'neutral' {
    const keys: string[] = [
        'primary',
        'secondary',
        'tertiary',
        'accent',
        'accentPrimary',
        'accentSecondary',
        'accentTertiary',
        'grey',
        'neutral',
    ];
    return keys.indexOf(value) !== -1;
}

export function isSystemColor(value: string): value is SystemColor {
    const keys: string[] = ['success', 'error', 'warning', 'info', 'highlight'];
    return keys.indexOf(value) !== -1;
}

export function isColorShade(value: string): value is ColorShade {
    const keys: string[] = ['base', 'light', 'lighter', 'lightest', 'dark'];
    return keys.indexOf(value) !== -1;
}

export function isColorTint(value: string): value is ColorTint {
    const keys: string[] = ['100', '200', '300', '400', '500', '600', '700'];
    return keys.indexOf(value) !== -1;
}

export function isBackgroundColor(value: string): value is BackgroundColor {
    const keys: string[] = ['body', 'content', 'offset'];
    return keys.indexOf(value) !== -1;
}

export function isCommonColor(value: string): value is CommonColor {
    const keys: string[] = ['white', 'black'];
    return keys.indexOf(value) !== -1;
}

export function isTextColor(value: string): value is TextColor {
    const keys: string[] = ['body', 'light', 'inverse', 'heading', 'price', 'link'];
    return keys.indexOf(value) !== -1;
}

export function isRootColor(value: string): value is RootColor {
    const keys: string[] = ['divider', 'border', 'starRating'];
    return keys.indexOf(value) !== -1;
}

export function isBodySize(value: string): value is TypographyBodySize {
    const keys: string[] = ['xs', 's', 'm', 'l', 'xl', '2xl'];
    return keys.indexOf(value) !== -1;
}

export function isHeadingSize(value: string): value is TypographyHeadingSize {
    const keys: string[] = ['2xs', 'xs', 's', 'm', 'l', 'xl'];
    return keys.indexOf(value) !== -1;
}

export function isTypographyVariant(value: string): value is TypographyVariant {
    const keys: string[] = [
        'body.xs',
        'body.s',
        'body.m',
        'body.l',
        'body.xl',
        'body.2xl',
        'body.3xl',
        'caption.xs',
        'caption.s',
        'caption.m',
        'caption.l',
        'caption.xl',
        'caption.2xl',
        'caption.3xl',
        'heading.xs',
        'heading.s',
        'heading.m',
        'heading.l',
        'heading.xl',
        'hero.2xs',
        'hero.xs',
        'hero.s',
        'hero.m',
        'hero.l',
        'hero.xl',
        'hero.2xl',
        'hero.3xl',
        'price.2xs',
        'price.xs',
        'price.s',
        'price.m',
        'price.l',
        'price.xl',
    ];
    return keys.indexOf(value) !== -1;
}

export function isButtonThemeableColor(value: string): value is ButtonThemeableColor {
    const keys: string[] = ['primary', 'secondary', 'callToAction'];
    return keys.indexOf(value) !== -1;
}

export function isButtonFixedColor(value: string): value is ButtonFixedColor {
    const keys: string[] = ['neutral', 'white'];
    return keys.indexOf(value) !== -1;
}

export function isSeatIconThemeableColor(value: string): value is SeatIconThemeableColor {
    const keys: string[] = ['available', 'availableHover', 'selected', 'selectedHover', 'unavailable'];
    return keys.indexOf(value) !== -1;
}

export function isChipThemeableColor(value: string): value is ChipThemeableColor {
    const keys: string[] = ['primary', 'secondary', 'tertiary'];
    return keys.indexOf(value) !== -1;
}

export function isChipFixedColor(value: string): value is ChipFixedColor {
    const keys: string[] = ['neutral', 'white', 'black'];
    return keys.indexOf(value) !== -1;
}

export function isFlagThemeableColor(value: string): value is FlagThemeableColor {
    const keys: string[] = ['primary', 'secondary', 'tertiary'];
    return keys.indexOf(value) !== -1;
}

export function isFlagFixedColor(value: string): value is FlagFixedColor {
    const keys: string[] = ['neutral', 'white', 'black'];
    return keys.indexOf(value) !== -1;
}

export function isScoreThemeableColor(value: string): value is ScoreThemeableColor {
    const keys: string[] = ['primary', 'secondary', 'tertiary'];
    return keys.indexOf(value) !== -1;
}

export function isScoreFixedColor(value: string): value is ScoreFixedColor {
    const keys: string[] = ['neutral', 'white', 'black'];
    return keys.indexOf(value) !== -1;
}

export function isStampThemeableColor(value: string): value is StampThemeableColor {
    const keys: string[] = ['primary', 'secondary', 'tertiary'];
    return keys.indexOf(value) !== -1;
}

export function isStampFixedColor(value: string): value is StampFixedColor {
    const keys: string[] = ['neutral', 'white', 'black'];
    return keys.indexOf(value) !== -1;
}

export function isIconButtonThemeableColor(value: string): value is IconButtonThemeableColor {
    const keys: string[] = ['primary', 'secondary', 'tertiary'];
    return keys.indexOf(value) !== -1;
}

export function isIconButtonFixedColor(value: string): value is IconButtonFixedColor {
    const keys: string[] = ['neutral', 'white', 'black'];
    return keys.indexOf(value) !== -1;
}

export function isCarouselThemeableColor(value: string): value is CarouselThemeableColor {
    const keys: string[] = ['primary', 'secondary', 'tertiary'];
    return keys.indexOf(value) !== -1;
}

export function isCarouselFixedColor(value: string): value is CarouselFixedColor {
    const keys: string[] = ['white'];
    return keys.indexOf(value) !== -1;
}

export function isActionButtonThemeableColor(value: string): value is ActionButtonThemeableColor {
    const keys: string[] = ['primary', 'secondary', 'tertiary'];
    return keys.indexOf(value) !== -1;
}

export function isActionButtonFixedColor(value: string): value is ActionButtonFixedColor {
    const keys: string[] = ['neutral', 'white'];
    return keys.indexOf(value) !== -1;
}

export function isProgressBarSize(value: string): value is ProgressBarSize {
    return ['xs', 's', 'm', 'l', 'xl'].includes(value);
}

export function isHoneycombRadius(value: string): value is HoneycombRadius {
    return ['none', '2xs', 'xs', 's', 'm', 'l'].includes(value);
}

export function isHoneycombShadowSize(value: string): value is HoneycombShadowSize {
    return ['none', 'xs', 's', 'm', 'l', 'xl'].includes(value);
}
